/**
 * Speech recognition scoring logic — ported from Unity C# implementation.
 *
 * Supports two evaluation paths:
 *   1. SpeechEvaluationSnippet (new path) — uses NGram/Levenshtein/StringComparison
 *      with pre-generated snippet data from the backend
 *   2. SpeakAlternativeToken (fallback) — uses Levenshtein + Jaccard N-gram similarity
 *      when snippet data is not available
 */

// ─── Constants ────────────────────────────────────────────────

export const SpeechEvaluationResultType = {
    NotPassed: 0,
    Passed: 1,
    Perfect: 2,
}

export const SpeechEvaluationMethodType = {
    NotDefined: 0,
    StringComparison: 1,
    Levenshtein: 2,
    NGram: 3,
}

const TWO_GRAM_THRESHOLD = 15
const MAX_NGRAM = 3
const DEFAULT_JACCARD_THRESHOLD = 0.65

const isDev = process.env.NODE_ENV !== 'production'
function devLog(...args) { if (isDev) console.log('[SpeechScoring]', ...args) }

// ─── Text Preprocessing ──────────────────────────────────────

const CHAR_BASE_FORM_MAP = {
    'à':'a','á':'a','â':'a','ã':'a','ä':'a','å':'a','ā':'a','ă':'a','ą':'a','æ':'a',
    'è':'e','é':'e','ê':'e','ë':'e','ē':'e','ĕ':'e','ė':'e','ę':'e','ě':'e',
    'ì':'i','í':'i','î':'i','ï':'i','ĩ':'i','ī':'i','ĭ':'i','į':'i','ı':'i',
    'ò':'o','ó':'o','ô':'o','õ':'o','ö':'o','ō':'o','ŏ':'o','ő':'o','ø':'o','œ':'o',
    'ù':'u','ú':'u','û':'u','ü':'u','ũ':'u','ū':'u','ŭ':'u','ů':'u','ű':'u','ų':'u',
    'ý':'y','ÿ':'y',
    'ñ':'n','ń':'n','ň':'n','ŋ':'n',
    'ç':'c','ć':'c','č':'c','ĉ':'c',
    'ś':'s','š':'s','ş':'s','ŝ':'s','ß':'s',
    'ź':'z','ž':'z','ż':'z',
    'ð':'d','ď':'d','đ':'d',
    'ĺ':'l','ľ':'l','ŀ':'l','ł':'l',
    'ŕ':'r','ř':'r',
    'ť':'t','ţ':'t','ŧ':'t','þ':'t',
    'ğ':'g','ĝ':'g','ġ':'g','ģ':'g',
    'ĥ':'h','ħ':'h',
    'ĵ':'j',
    'ķ':'k',
    '\u2018':'\'', '\u2019':'\'', '\u201A':'\'', '\u201B':'\'',
    '\u201C':'"', '\u201D':'"', '\u201E':'"', '\u00AB':'"', '\u00BB':'"',
}

function getBaseFormOfCharacter(ch) {
    return CHAR_BASE_FORM_MAP[ch] || ch
}

// ─── Polish Number Mapping (mirrors backend PolishNumbersMapper) ─────

export const POLISH_LANGUAGE_ID = 10

const POLISH_WORD_TO_DIGIT = {
    'jeden': '1', 'jedna': '1', 'jedno': '1',
    'pierwszy': '1.',
    'dwa': '2', 'dwaj': '2', 'dwie': '2', 'dwoje': '2',
    'drugi': '2.',
    'trzy': '3', 'trzej': '3', 'trzech': '3',
    'trzeci': '3.',
    'cztery': '4', 'czterej': '4', 'czterech': '4', 'czworo': '4',
    'czwarty': '4.',
    'pięć': '5', 'piąty': '5.',
    'sześć': '6', 'szósty': '6.',
    'siedem': '7', 'siódmy': '7.',
    'osiem': '8', 'ósmy': '8.',
    'dziewięć': '9', 'dziewiąty': '9.',
    'dziesięć': '10', 'dziesiąty': '10.',
    'jedenaście': '11', 'jedenasty': '11.',
    'dwanaście': '12', 'dwunasty': '12.',
    'trzynaście': '13', 'trzynasty': '13.',
    'czternaście': '14', 'czternasty': '14.',
    'piętnaście': '15', 'piętnasty': '15.',
    'szesnaście': '16', 'szesnasty': '16.',
    'siedemnaście': '17', 'siedemnasty': '17.',
    'osiemnaście': '18', 'osiemnasty': '18.',
    'dziewiętnaście': '19', 'dziewiętnasty': '19.',
    'dwadzieścia': '20', 'dwudziesty': '20.',
    'trzydzieści': '30', 'trzydziesty': '30.',
    'czterdzieści': '40', 'czterdziesty': '40.',
    'pięćdziesiąt': '50', 'pięćdziesiąty': '50.',
    'sześćdziesiąt': '60', 'sześćdziesiąty': '60.',
    'siedemdziesiąt': '70', 'siedemdziesiąty': '70.',
    'osiemdziesiąt': '80', 'osiemdziesiąty': '80.',
    'dziewięćdziesiąt': '90', 'dziewięćdziesiąty': '90.',
    'sto': '100', 'setny': '100.',
    'tysiąc': '1000', 'tysięczny': '1000.',
}

const POLISH_DIGIT_TO_WORD = {
    '0': 'zero',
    '1': 'jeden',
    '2': 'dwa',
    '3': 'trzy',
    '4': 'cztery',
    '5': 'pięć',
    '6': 'sześć',
    '7': 'siedem',
    '8': 'osiem',
    '9': 'dziewięć',
}

/**
 * Replace single-digit tokens with their Polish word equivalents.
 * Called before normalizeTokens to prevent digit merging (e.g. "4","7","1","1" → "4711").
 */
function expandPolishDigitTokens(tokens) {
    return tokens.map((t) => {
        if (/^\d$/.test(t) && POLISH_DIGIT_TO_WORD[t]) return POLISH_DIGIT_TO_WORD[t]
        return t
    })
}

/**
 * Generate digit-replaced variants of a Polish snippet text.
 * Mirrors backend GetPolishHomophoneStrings but with bug fixes:
 * case-insensitive, hyphens treated as spaces, word-boundary matching.
 */
export function getPolishHomophoneStrings(snippetText) {
    const normalized = snippetText.trim().toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ')

    if (POLISH_WORD_TO_DIGIT[normalized]) {
        return [POLISH_WORD_TO_DIGIT[normalized]]
    }

    const words = normalized.split(' ')
    const matchIndices = []

    for (let i = 0; i < words.length; i++) {
        if (POLISH_WORD_TO_DIGIT[words[i]]) {
            matchIndices.push(i)
        }
    }

    if (matchIndices.length === 0) return []

    const texts = []
    for (const idx of matchIndices) {
        const replaced = [...words]
        replaced[idx] = POLISH_WORD_TO_DIGIT[words[idx]]
        texts.push(replaced.join(' '))
    }

    if (matchIndices.length > 1) {
        const allReplaced = [...words]
        for (const idx of matchIndices) {
            allReplaced[idx] = POLISH_WORD_TO_DIGIT[words[idx]]
        }
        texts.push(allReplaced.join(' '))
    }

    return texts
}

function normalizeSnippet(snippet) {
    const lowered = snippet.toLowerCase()
    let result = ''
    for (let i = 0; i < lowered.length; i++) {
        const base = getBaseFormOfCharacter(lowered[i])
        const code = base.charCodeAt(0)
        if (
            (code >= 0x0061 && code <= 0x007A) || // a-z
            (code >= 0x00DF && code <= 0x00F6) || // Latin-1 supplement
            (code >= 0x00F8 && code <= 0x00FF) ||
            (code >= 0x0030 && code <= 0x0039) || // 0-9
            code === 0x0020                        // space
        ) {
            result += base
        } else if (code === 0x002D) {
            result += ' '
        }
    }
    return result
}

function normalizeTokens(tokens) {
    const normalized = tokens
        .map(normalizeSnippet)
        .filter((t) => t.trim().length > 0)

    const merged = []
    let i = 0
    while (i < normalized.length) {
        if (/^\d+$/.test(normalized[i])) {
            let combined = normalized[i]
            let j = i + 1
            while (j < normalized.length && /^\d+$/.test(normalized[j])) {
                combined += normalized[j]
                j++
            }
            merged.push(combined)
            i = j
        } else {
            merged.push(normalized[i])
            i++
        }
    }
    return merged
}

export function createTokensFromSnippet(snippet, languageId) {
    let tokens = snippet.toLowerCase().split(' ').filter(Boolean)
    if (languageId === POLISH_LANGUAGE_ID) {
        tokens = expandPolishDigitTokens(tokens)
    }
    return normalizeTokens(tokens)
}

export function preProcessTextForSpeechRecognition(text) {
    if (!text?.trim()) return ''

    const tokens = text.split(' ')
    const trimmedTokens = []
    for (const token of tokens) {
        let trimmed = token.replace(/[^0-9\p{L}\- ]+/gu, '').toLowerCase()
        trimmed = trimmed.replace(/-/g, ' ').trim()
        if (trimmed) trimmedTokens.push(trimmed)
    }
    return trimmedTokens.join(' ').toLowerCase()
}

// ─── N-Gram Generation ───────────────────────────────────────

export function produceNGrams(text, n) {
    if (!text?.trim()) return []
    const count = text.length - n + 1
    if (count <= 0) return []

    const result = []
    for (let i = 0; i < count; i++) {
        result.push(text.substring(i, i + n))
    }
    return result
}

function produceNGramsWithPreProcessing(text, n) {
    const stripped = text.replace(/[^0-9\p{L} ']+/gu, '').toLowerCase()
    return produceNGrams(stripped, n)
}

function produceWordTokenVector(text, n) {
    const stripped = text.replace(/[^0-9\p{L} ']+/gu, '').toLowerCase()
    const words = stripped.split(' ').filter(Boolean)
    const count = words.length - n + 1
    if (count <= 0) return []

    const result = []
    for (let i = 0; i < count; i++) {
        result.push(words.slice(i, i + n).join(' '))
    }
    return result
}

// ─── Fallback Token Generation (port of backend SpeechEvaluationLogic) ───

const FALLBACK_GLOBAL_NAIVE_THRESHOLD = 0.65
const FALLBACK_TOKEN_THRESHOLD = 0.8
const FALLBACK_HOMOPHONE_THRESHOLD = 0.8

function createWordBoundaryNGrams(prevWord, nGramN, currentWord, nextWord) {
    const nGrams = []

    if (prevWord) {
        const prevWordEnding = prevWord.substring(prevWord.length - nGramN + 2)
        const wordStart = currentWord.substring(0, nGramN - 2)
        nGrams.push(...produceNGrams(`${prevWordEnding} ${wordStart}`, nGramN))
    }

    if (nextWord) {
        const wordEnding = currentWord.substring(currentWord.length - nGramN + 2)
        const nextWordStart = nextWord.substring(0, nGramN - 2)
        nGrams.push(...produceNGrams(`${wordEnding} ${nextWordStart}`, nGramN))
    }

    return nGrams
}

/**
 * Generate token data on the fly when the backend snippet has empty tokens.
 * Mirrors backend GenerateSpeechEvaluationSnippetVersion1Dtos (NGram branch)
 * but without homophones — each word is its own single-homophone token.
 * Returns null for snippets with <= 2 words (those use the Levenshtein fallback).
 */
function generateFallbackTokens(snippetText) {
    const normalized = normalizeSnippet(snippetText)
    if (!normalized) return null

    const words = normalized.split(' ').filter(Boolean)
    if (words.length <= 2) return null

    const textLen = normalized.length
    const nGramN = textLen < TWO_GRAM_THRESHOLD ? 2 : 3

    const tokens = []
    let prevShortestLen = 0
    let prevWordStartIndex = 0
    let prevEndIndex = 0

    for (let i = 0; i < words.length; i++) {
        const word = words[i]
        const prevWord = i > 0 ? words[i - 1] : null
        const nextWord = i < words.length - 1 ? words[i + 1] : null
        const wordLen = word.length

        let startIndex
        if (prevWord) {
            startIndex = prevWordStartIndex + prevShortestLen
            prevWordStartIndex = startIndex + 1
            if (prevShortestLen !== nGramN - 1) {
                startIndex -= nGramN - 2
            }
        } else {
            startIndex = 0
            prevWordStartIndex = 0
        }

        const prefix = prevWord ? ' ' : ''
        const suffix = nextWord ? ' ' : ''

        let wordNGrams
        if (nGramN === 2) {
            wordNGrams = produceNGrams(`${prefix}${word}${suffix}`, nGramN)
        } else {
            wordNGrams = createWordBoundaryNGrams(prevWord, nGramN, word, nextWord)
            wordNGrams.push(...produceNGrams(`${prefix}${word}${suffix}`, nGramN))
        }
        wordNGrams = [...new Set(wordNGrams)]

        let endIndex = prevEndIndex + wordLen
        if (nGramN > 2) {
            endIndex += nextWord ? nGramN - 2 : -1
        } else {
            if (prevWord && nextWord) endIndex += 1
        }

        prevEndIndex = endIndex
        prevShortestLen = wordLen

        tokens.push({
            startIndex,
            endIndex,
            homophones: [{ text: word, nGrams: wordNGrams }],
        })
    }

    return {
        tokens,
        nGramLength: nGramN,
        snippetText: normalized,
        speechEvaluationParams: {
            speechEvaluationMethodType: SpeechEvaluationMethodType.NGram,
            globalNaiveAssociationSimilarityThreshold: FALLBACK_GLOBAL_NAIVE_THRESHOLD,
            tokenAssociationSimilarityThreshold: FALLBACK_TOKEN_THRESHOLD,
            homophoneAssociationSimilarityThreshold: FALLBACK_HOMOPHONE_THRESHOLD,
        },
    }
}

// ─── Similarity Measures ─────────────────────────────────────

export function computeJaccardSimilarity(targetVec, vec) {
    let intersections = 0
    for (const token of targetVec) {
        for (const other of vec) {
            if (token === other) {
                intersections++
                break
            }
        }
    }
    const union = targetVec.length + vec.length - intersections
    return union === 0 ? 0 : intersections / union
}

export function computeEditDistance(s, t) {
    const n = s.length
    const m = t.length
    if (n === 0) return m
    if (m === 0) return n

    const d = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))
    for (let i = 0; i <= n; i++) d[i][0] = i
    for (let j = 0; j <= m; j++) d[0][j] = j

    for (let i = 1; i <= n; i++) {
        for (let j = 1; j <= m; j++) {
            const cost = s[i - 1] === t[j - 1] ? 0 : 1
            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost)
        }
    }
    return d[n][m]
}

function getLevenshteinThreshold(targetLength) {
    if (targetLength <= 2) return 0
    if (targetLength <= 4) return 1
    if (targetLength <= 6) return 2
    return 4
}

function doLevenshtein(speechToken, targetToken) {
    const editDistance = computeEditDistance(targetToken, speechToken)

    if (editDistance === 0) {
        return { method: SpeechEvaluationMethodType.Levenshtein, result: SpeechEvaluationResultType.Perfect, score: 1 }
    }

    const similarity = targetToken.length > 0 ? 1 - editDistance / targetToken.length : 0
    const threshold = getLevenshteinThreshold(targetToken.length)

    if (editDistance <= threshold) {
        const allDigits = (s) => /^[\d ]+$/.test(s)
        if (allDigits(targetToken) && allDigits(speechToken)) {
            return { method: SpeechEvaluationMethodType.Levenshtein, result: SpeechEvaluationResultType.NotPassed, score: similarity }
        }
        return { method: SpeechEvaluationMethodType.Levenshtein, result: SpeechEvaluationResultType.Passed, score: similarity }
    }

    return { method: SpeechEvaluationMethodType.Levenshtein, result: SpeechEvaluationResultType.NotPassed, score: similarity }
}

// ─── SpeakAlternativeToken (Fallback Path) ───────────────────

export class SpeakAlternativeToken {
    constructor(text, alternativeIdx = -1) {
        this.originalText = text
        this.originalTextWordCount = (text.match(/ /g) || []).length + 1

        const words = text.split(' ').filter(Boolean)
        this.wordTokensNGramSize = Math.min(words.length, MAX_NGRAM)

        const textLen = text.length
        if (textLen < TWO_GRAM_THRESHOLD && textLen > 1) {
            this.characterNGramSize = 2
        } else if (textLen > 1) {
            this.characterNGramSize = MAX_NGRAM
        } else {
            this.characterNGramSize = textLen
        }

        this.wordTokens = produceWordTokenVector(text, this.wordTokensNGramSize)
        this.characterTokens = produceNGramsWithPreProcessing(text, this.characterNGramSize)
        this.wordTokensWeight = 0
        this.characterTokensWeight = 1
        this.alternativeIdx = alternativeIdx
    }

    static fromTextAndTemplate(text, template) {
        if (text.length < template.characterNGramSize) return null
        if (text.split(' ').filter(Boolean).length < template.wordTokensNGramSize) return null

        const token = new SpeakAlternativeToken.__empty()
        token.wordTokensNGramSize = template.wordTokensNGramSize
        token.characterNGramSize = template.characterNGramSize
        token.wordTokens = produceWordTokenVector(text, template.wordTokensNGramSize)
        token.characterTokens = produceNGramsWithPreProcessing(text, template.characterNGramSize)
        token.wordTokensWeight = 0
        token.characterTokensWeight = 1
        token.alternativeIdx = template.alternativeIdx
        return token
    }

    computeJaccardSimilarity(other) {
        return computeJaccardSimilarity(this.characterTokens, other.characterTokens) * this.characterTokensWeight
    }
}

// Private constructor for template-based creation
SpeakAlternativeToken.__empty = class {
    constructor() {
        this.originalText = ''
        this.originalTextWordCount = 0
    }
    computeJaccardSimilarity(other) {
        return computeJaccardSimilarity(this.characterTokens, other.characterTokens) * this.characterTokensWeight
    }
}

/**
 * Evaluate speech tokens against a target alternative using Levenshtein + Jaccard similarity.
 * This is the fallback path used when SpeechEvaluationSnippets are not available.
 */
export function associateSpeechWithAlternativeToken(
    speechResultWords,
    targetToken,
    jaccardThreshold = DEFAULT_JACCARD_THRESHOLD,
    stopWhenSatisfied = false
) {
    devLog('AlternativeToken eval:', { target: targetToken.originalText, speech: speechResultWords.join(' '), jaccardThreshold })
    let highestScore = -1

    let range = speechResultWords.length - targetToken.originalTextWordCount + 1
    let subRange = targetToken.originalTextWordCount
    if (range <= 0) {
        range = 1
        subRange = speechResultWords.length
    }

    for (let j = 0; j < range; j++) {
        const subSpeech = speechResultWords.slice(j, j + subRange)

        const LEVENSHTEIN_THRESHOLD_FOR_EXECUTION = 12
        if (targetToken.originalText.length <= LEVENSHTEIN_THRESHOLD_FOR_EXECUTION) {
            const fullStr = subSpeech.join(' ')
            const levResult = doLevenshtein(fullStr, targetToken.originalText)
            if (levResult.result !== SpeechEvaluationResultType.NotPassed) {
                highestScore = 1
                if (stopWhenSatisfied) {
                    return {
                        method: SpeechEvaluationMethodType.Levenshtein,
                        result: SpeechEvaluationResultType.Passed,
                        score: 1,
                    }
                }
            }
        }

        const resultText = subSpeech.join(' ')
        if (resultText.length < targetToken.characterNGramSize) continue
        if (resultText.split(' ').filter(Boolean).length < targetToken.wordTokensNGramSize) continue

        const resultToken = new SpeakAlternativeToken(resultText, targetToken.alternativeIdx)
        const similarity = targetToken.computeJaccardSimilarity(resultToken)

        if (similarity > highestScore) {
            highestScore = similarity
            if (stopWhenSatisfied && highestScore >= jaccardThreshold) {
                return {
                    method: SpeechEvaluationMethodType.NGram,
                    result: SpeechEvaluationResultType.Passed,
                    score: highestScore,
                }
            }
        }
    }

    const finalResult = {
        method: SpeechEvaluationMethodType.NGram,
        result: highestScore >= jaccardThreshold ? SpeechEvaluationResultType.Passed : SpeechEvaluationResultType.NotPassed,
        score: highestScore,
    }
    devLog('AlternativeToken result:', { result: finalResult.result, score: finalResult.score })
    return finalResult
}

// ─── SpeechEvaluationSnippet Path (New) ──────────────────────

/**
 * Create a snippet tracker with hit-tracking state for NGram evaluation.
 */
export function createSnippetTracker(targetSnippet, alternativeIdx) {
    let effectiveSnippet = targetSnippet

    if (!targetSnippet.tokens || targetSnippet.tokens.length === 0) {
        const generated = generateFallbackTokens(targetSnippet.snippetText)
        if (generated) {
            devLog('createSnippetTracker: generated fallback tokens for', targetSnippet.snippetText)
            effectiveSnippet = { ...targetSnippet, ...generated }
        }
    }

    const originalNGrams = [...new Set(produceNGrams(effectiveSnippet.snippetText, effectiveSnippet.nGramLength))]

    const tokenTrackers = (effectiveSnippet.tokens || []).map((token) => {
        const homophoneTrackers = (token.homophones || []).map((homophone) => {
            const uniqueNGrams = []
            const seen = new Set()
            for (const ng of homophone.nGrams || []) {
                if (!seen.has(ng)) {
                    seen.add(ng)
                    uniqueNGrams.push({ nGram: ng, hit: false })
                }
            }
            return {
                wordText: homophone.text,
                wordCount: (homophone.text.match(/ /g) || []).length + 1,
                requiredHits: Math.max(homophone.text.length - 2, 1),
                nGramTrackers: uniqueNGrams,
                hitScore: 0,
            }
        })
        return {
            startIndex: token.startIndex,
            endIndex: token.endIndex,
            homophoneTrackers,
            hitScore: 0,
        }
    })

    return {
        speechEvaluationSnippet: effectiveSnippet,
        tokenTrackers,
        originalSnippetNGrams: originalNGrams,
        tokenTotalHitPercent: 0,
        jaccardSimilarityScore: 0,
        alternativeIdx,
        resetScores() {
            this.tokenTotalHitPercent = 0
            this.jaccardSimilarityScore = 0
            for (const tt of this.tokenTrackers) {
                tt.hitScore = 0
                for (const ht of tt.homophoneTrackers) {
                    ht.hitScore = 0
                    for (const ng of ht.nGramTrackers) {
                        ng.hit = false
                    }
                }
            }
        },
    }
}

function associateSpeechWithStringComparison(speechTokens, targetSnippet) {
    for (const word of speechTokens) {
        if (word === targetSnippet.snippetText) {
            return { method: SpeechEvaluationMethodType.StringComparison, result: SpeechEvaluationResultType.Perfect, score: 1 }
        }
        for (const token of targetSnippet.tokens || []) {
            for (const homophone of token.homophones || []) {
                if (word === homophone.text) {
                    return { method: SpeechEvaluationMethodType.StringComparison, result: SpeechEvaluationResultType.Perfect, score: 1 }
                }
            }
        }
    }
    return { method: SpeechEvaluationMethodType.StringComparison, result: SpeechEvaluationResultType.NotPassed, score: 0 }
}

function associateSpeechWithLevenshteinSnippet(speechTokens, snippetTracker) {
    if (snippetTracker.tokenTrackers.length === 0) {
        return { method: SpeechEvaluationMethodType.Levenshtein, result: SpeechEvaluationResultType.NotPassed, score: 0 }
    }
    for (const tokenTracker of snippetTracker.tokenTrackers) {
        for (const ht of tokenTracker.homophoneTrackers) {
            let range = speechTokens.length - ht.wordCount
            let subRange = ht.wordCount
            if (range <= 0) {
                range = 1
                subRange = speechTokens.length
            }

            for (let i = 0; i < range; i++) {
                const subSpeech = speechTokens.slice(i, i + subRange).join(' ')
                const levResult = doLevenshtein(subSpeech, ht.wordText)
                if (levResult.result !== SpeechEvaluationResultType.NotPassed) return levResult

                if (i + subRange < speechTokens.length) {
                    const extSubSpeech = speechTokens.slice(i, i + subRange + 1).join(' ')
                    const extResult = doLevenshtein(extSubSpeech, ht.wordText)
                    if (extResult.result !== SpeechEvaluationResultType.NotPassed) return extResult
                }
            }
        }
    }
    return { method: SpeechEvaluationMethodType.Levenshtein, result: SpeechEvaluationResultType.NotPassed, score: 0 }
}

function associateSpeechWithNGramSnippet(speechTokens, snippetTracker) {
    const snippet = snippetTracker.speechEvaluationSnippet
    const params = snippet.speechEvaluationParams

    if (snippetTracker.tokenTrackers.length === 0) {
        devLog('NGram: no tokenTrackers — snippet has empty/null tokens, returning NotPassed', snippet.snippetText)
        return { method: SpeechEvaluationMethodType.NGram, result: SpeechEvaluationResultType.NotPassed, score: 0 }
    }

    let wordCount = 0
    for (const tt of snippetTracker.tokenTrackers) {
        wordCount += Math.max(...tt.homophoneTrackers.map((h) => h.wordCount))
    }

    let range = speechTokens.length - wordCount + 1
    let subRange = wordCount
    if (range <= 0) {
        range = 1
        subRange = speechTokens.length
    }

    let highestGlobalNaive = 0
    let highestSimilarity = 0

    for (let i = 0; i < range; i++) {
        const subSpeech = speechTokens.slice(i, i + subRange).join(' ')
        const subNGrams = produceNGrams(subSpeech, snippet.nGramLength)

        let nGramStartIdx = 0
        let nGramEndIdx = snippet.nGramLength - 1

        for (const speechNGram of subNGrams) {
            for (const tt of snippetTracker.tokenTrackers) {
                if (
                    tt.hitScore >= params.tokenAssociationSimilarityThreshold ||
                    nGramStartIdx < tt.startIndex ||
                    nGramEndIdx > tt.endIndex
                ) {
                    continue
                }

                for (const ht of tt.homophoneTrackers) {
                    if (ht.requiredHits <= 0) continue
                    if (ht.hitScore / ht.requiredHits >= params.homophoneAssociationSimilarityThreshold) continue

                    for (const ngt of ht.nGramTrackers) {
                        if (!ngt.hit && ngt.nGram === speechNGram) {
                            ngt.hit = true
                            ht.hitScore++
                            break
                        }
                    }
                }

                tt.hitScore = Math.max(...tt.homophoneTrackers.map((h) => (h.requiredHits > 0 ? h.hitScore / h.requiredHits : 0)))
            }

            if (snippetTracker.tokenTrackers.length > 0) {
                const sim = snippetTracker.tokenTrackers.reduce((s, t) => s + t.hitScore, 0) / snippetTracker.tokenTrackers.length
                if (sim > highestSimilarity) highestSimilarity = sim
            }

            nGramStartIdx++
            nGramEndIdx++
        }

        if (snippetTracker.tokenTrackers.every((t) => t.hitScore >= params.tokenAssociationSimilarityThreshold)) {
            return {
                method: SpeechEvaluationMethodType.NGram,
                result: SpeechEvaluationResultType.Perfect,
                score: highestSimilarity,
                naiveScore: highestGlobalNaive,
            }
        }

        if (highestGlobalNaive < params.globalNaiveAssociationSimilarityThreshold) {
            const distinctSubNGrams = [...new Set(subNGrams)]
            const jaccardSim = computeJaccardSimilarity(snippetTracker.originalSnippetNGrams, distinctSubNGrams)
            if (jaccardSim > highestGlobalNaive) highestGlobalNaive = jaccardSim
        }
    }

    if (highestGlobalNaive > params.globalNaiveAssociationSimilarityThreshold) {
        return {
            method: SpeechEvaluationMethodType.NGram,
            result: SpeechEvaluationResultType.Passed,
            score: highestSimilarity,
            naiveScore: highestGlobalNaive,
        }
    }

    return {
        method: SpeechEvaluationMethodType.NGram,
        result: SpeechEvaluationResultType.NotPassed,
        score: highestSimilarity,
        naiveScore: highestGlobalNaive,
    }
}

function fallbackLevenshteinForSnippet(speechTokens, snippetText) {
    const targetText = preProcessTextForSpeechRecognition(snippetText)
    if (!targetText) {
        return { method: SpeechEvaluationMethodType.Levenshtein, result: SpeechEvaluationResultType.NotPassed, score: 0 }
    }

    const targetWordCount = targetText.split(' ').filter(Boolean).length

    let range = speechTokens.length - targetWordCount + 1
    let subRange = targetWordCount
    if (range <= 0) {
        range = 1
        subRange = speechTokens.length
    }

    for (let i = 0; i < range; i++) {
        const subSpeech = speechTokens.slice(i, i + subRange).join(' ')
        const levResult = doLevenshtein(subSpeech, targetText)
        if (levResult.result !== SpeechEvaluationResultType.NotPassed) return levResult

        if (i + subRange < speechTokens.length) {
            const extSubSpeech = speechTokens.slice(i, i + subRange + 1).join(' ')
            const extResult = doLevenshtein(extSubSpeech, targetText)
            if (extResult.result !== SpeechEvaluationResultType.NotPassed) return extResult
        }
    }

    const fullSpeech = speechTokens.join(' ')
    return doLevenshtein(fullSpeech, targetText)
}

/**
 * Evaluate speech against a snippet tracker using the method defined in the snippet params.
 */
export function associateSpeechWithSnippetTracker(speechTokens, snippetTracker) {
    snippetTracker.resetScores()

    const method = snippetTracker.speechEvaluationSnippet.speechEvaluationParams?.speechEvaluationMethodType

    devLog('SnippetTracker eval:', {
        snippetText: snippetTracker.speechEvaluationSnippet.snippetText,
        method,
        tokenCount: snippetTracker.tokenTrackers.length,
        speechTokens,
    })

    if (snippetTracker.tokenTrackers.length === 0) {
        devLog('SnippetTracker: no tokenTrackers, falling back to Levenshtein on snippetText')
        const result = fallbackLevenshteinForSnippet(speechTokens, snippetTracker.speechEvaluationSnippet.snippetText)
        devLog('SnippetTracker fallback result:', { result: result.result, score: result.score })
        return result
    }

    let result
    switch (method) {
        case SpeechEvaluationMethodType.StringComparison:
            result = associateSpeechWithStringComparison(speechTokens, snippetTracker.speechEvaluationSnippet)
            break
        case SpeechEvaluationMethodType.Levenshtein:
            result = associateSpeechWithLevenshteinSnippet(speechTokens, snippetTracker)
            break
        case SpeechEvaluationMethodType.NGram:
            result = associateSpeechWithNGramSnippet(speechTokens, snippetTracker)
            break
        default:
            result = { method: SpeechEvaluationMethodType.NotDefined, result: SpeechEvaluationResultType.NotPassed, score: 0 }
    }

    devLog('SnippetTracker result:', { result: result.result, score: result.score, method: result.method })
    return result
}
